import { useIntl } from 'react-intl';

import { useGetVehicleStateQuery } from '@/services/dfpFrontendApi';
import type { DataMockFileMeta, ScheduleSummary, VehicleConfig } from '../../types';
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection';
import AssetInfoPanel from './AssetInfoPanel';
import ConfiguredMessagesList from './ConfiguredMessagesList';
import DiagnosticsSection from './DiagnosticsSection';
import MockFilesGrid from './MockFilesGrid';

interface VehicleDetailPanelProps {
    vehicle: VehicleConfig;
    onViewMessageConfig: (schedule: ScheduleSummary) => void;
    onOpenSourceFile: (file: DataMockFileMeta) => void;
    onOpenInArbitrary: (schedule: ScheduleSummary, mergedJson: string, overriddenJsonPaths: string[]) => void;
}

const VehicleDetailPanel = ({
    vehicle,
    onViewMessageConfig,
    onOpenSourceFile,
    onOpenInArbitrary,
}: VehicleDetailPanelProps) => {
    const intl = useIntl();
    const { data: vehicleState } = useGetVehicleStateQuery(vehicle.vin, {
        pollingInterval: 30000,
    });

    return (
        <div className="border-top border-color-light">
            <CollapsibleSection
                title={intl.formatMessage({ id: 'intl-msg:vehicleDetail.assetInformation' })}
                icon="icon-info-sign"
                defaultOpen
            >
                <AssetInfoPanel vehicle={vehicle} />
            </CollapsibleSection>

            <CollapsibleSection
                title={intl.formatMessage({ id: 'intl-msg:vehicleDetail.overrideVariables' })}
                icon="icon-cog"
            >
                <DiagnosticsSection
                    stateConfig={vehicle.state_config}
                    currentState={vehicleState?.state}
                    mileage={vehicleState?.mileage ?? null}
                />
            </CollapsibleSection>

            <CollapsibleSection
                title={intl.formatMessage({ id: 'intl-msg:vehicleDetail.configuredMessages' })}
                icon="icon-layer"
            >
                <ConfiguredMessagesList
                    vin={vehicle.vin}
                    schedules={vehicle.schedules}
                    onViewConfig={onViewMessageConfig}
                    onOpenInArbitrary={onOpenInArbitrary}
                />
            </CollapsibleSection>

            <CollapsibleSection
                title={intl.formatMessage({ id: 'intl-msg:vehicleDetail.dataMockSourceFiles' })}
                icon="icon-folder-open"
            >
                <MockFilesGrid files={vehicle.data_mock_files} onOpenFile={onOpenSourceFile} />
            </CollapsibleSection>
        </div>
    );
};

export default VehicleDetailPanel;
